/**
 * California State-Specific Diagnostic Questions
 * All questions reference actual CA regulations, forms, and requirements
 * Regulatory Body: California Department of Social Services (CDSS) - Home Care Services Bureau (HCSB)
 * Primary Law: Home Care Services Consumer Protection Act (HSC 1796.10-1796.63)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const CALIFORNIA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'CA-A1',
    itemId: 'A1',
    stateCode: 'CA',
    title: 'CDSS Home Care Organization License Status',
    description: 'California requires all home care organizations to be licensed by CDSS under the Home Care Services Consumer Protection Act',
    severity: 'high',
    regulationCode: 'HSC 1796.35',
    regulationName: 'License',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov/inforesources/community-care/home-care-services',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Organization license from CDSS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HCO licenses must be renewed every 2 years. Check your license status with CDSS.',
      },
      {
        id: 'q2',
        question: 'What is your HCO license expiration date?',
        inputType: 'date',
        helpText: 'Found on your CDSS license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your CDSS Home Care Organization license is current and active per HSC 1796.35.',
      atRisk: 'Your HCO license expires within 120 days. California requires renewal applications to be submitted to CDSS before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home care organization in California without a valid CDSS license violates HSC 1796.35 and can result in civil penalties up to $10,000 per violation. Contact CDSS immediately at (877) 424-5778.',
      unknown: 'Verify your HCO license status at cdss.ca.gov or call CDSS at (877) 424-5778.',
    },
    stateNotes: [
      'HCO licenses are renewed every 2 years',
      'CDSS conducts inspections prior to licensure',
      'Application fee varies based on organization size',
      'Online orientation required after application submission',
    ],
  },

  {
    id: 'CA-A2',
    itemId: 'A2',
    stateCode: 'CA',
    title: 'Home Care Aide Registry Verification',
    description: 'California requires all affiliated home care aides to be registered on the HCA Registry',
    severity: 'high',
    regulationCode: 'HSC 1796.23',
    regulationName: 'HCA Registry',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov/inforesources/community-care/home-care-services',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL your affiliated Home Care Aides registered on the California HCA Registry?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% are registered and verified' },
          { value: 'most', label: 'Most are registered, some pending' },
          { value: 'some', label: 'Some are not registered' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'HCAs must be registered BEFORE having contact with clients per HSC 1796.23',
      },
      {
        id: 'q2',
        question: 'Do you verify HCA registry status BEFORE assigning aides to clients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always verify first' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'no'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs are properly registered on the California HCA Registry per HSC 1796.23.',
      atRisk: 'Some HCAs may be pending registration or verification is inconsistent. Per HSC 1796.23, HCAs must be registered BEFORE client contact.',
      nonCompliant: 'Using unregistered HCAs violates HSC 1796.23 and can result in license revocation. Immediately verify all HCA registry status and remove unregistered aides from client assignments.',
      unknown: 'Verify all HCA registry status at cdss.ca.gov',
    },
    stateNotes: [
      'HSC 1796.23 requires HCA registration BEFORE any client contact',
      'Use CDSS Guardian system to verify "Eligible" status for each HCA',
      'HCA Registry renewals are required every 2 years per HSC 1796.23(d)',
      'Unregistered HCAs expose the HCO to civil penalties up to $10,000 per violation',
      'Tip: Implement a verification checklist before each new client assignment',
    ],
  },

  {
    id: 'CA-A3',
    itemId: 'A3',
    stateCode: 'CA',
    title: 'Insurance Requirements (HSC 1796.42)',
    description: 'California requires specific insurance coverage including liability, workers\' comp, and dishonesty bond',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Insurance',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'What is your general and professional liability insurance per-occurrence coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'California requires minimum $1,000,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'What is your general and professional liability insurance aggregate coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'California requires minimum $3,000,000 aggregate',
      },
      {
        id: 'q3',
        question: 'Do you maintain a valid workers\' compensation policy covering all affiliated HCAs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation is REQUIRED in California per HSC 1796.42',
      },
      {
        id: 'q4',
        question: 'What is your employee dishonesty bond coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'California requires minimum $10,000 dishonesty bond with third-party coverage',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'greaterOrEqual', questionId: 'q4', value: 10000 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 2000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'lessThan', questionId: 'q1', value: 1000000 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets California HSC 1796.42 requirements: $1M per occurrence, $3M aggregate liability, workers\' compensation, and $10K dishonesty bond.',
      atRisk: 'Your insurance may not meet all California requirements. HSC 1796.42 requires: $1M per occurrence, $3M aggregate, workers\' comp, and $10K dishonesty bond.',
      nonCompliant: 'Your insurance coverage does not meet California requirements per HSC 1796.42. Missing or inadequate coverage can result in license revocation.',
      unknown: 'Verify your insurance meets HSC 1796.42 requirements.',
    },
    stateNotes: [
      'California requires $1M per occurrence AND $3M aggregate - both are mandatory',
      'Workers\' compensation is REQUIRED - no exceptions',
      'Dishonesty bond must include third-party coverage',
      'Proof of insurance must be submitted with license renewal',
    ],
  },

  {
    id: 'CA-A4',
    itemId: 'A4',
    stateCode: 'CA',
    title: 'Written Policies and Procedures',
    description: 'California requires specific written policies per HSC 1796.42',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Operations',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following California-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Client rights and responsibilities' },
          { value: 'service_agreement', label: 'Written service agreement requirements' },
          { value: 'aide_duties', label: 'Home care aide duties and limitations' },
          { value: 'supervision', label: 'Supervision of home care aides' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Live Scan background check procedures' },
          { value: 'training', label: 'Training requirements (HSC 1796.44)' },
          { value: 'registry', label: 'HCA Registry enrollment procedures' },
          { value: 'abuse_reporting', label: 'Mandated reporter procedures (SOC 341)' },
          { value: 'records', label: 'Record retention (7 years per California law)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets California HSC 1796.42 requirements.',
      atRisk: 'Your policy manual is missing required sections per HSC 1796.42. CDSS inspectors will review your policies for completeness. Missing policies: {missing}.',
      nonCompliant: 'Multiple required policies are missing per HSC 1796.42. This is a common CDSS inspection deficiency. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against HSC 1796.42 requirements.',
    },
    stateNotes: [
      'HSC 1796.42 mandates specific written policies for all HCOs',
      'CDSS inspectors commonly cite incomplete policy manuals during audits',
      'Policies must be reviewed and updated annually per best practice',
      'Tip: Use CDSS sample policies as templates for required documentation',
      'Keep signed acknowledgment forms showing staff have read all policies',
    ],
  },

  {
    id: 'CA-A5',
    itemId: 'A5',
    stateCode: 'CA',
    title: 'Incident and Abuse Reporting Process (HSC 1796.42)',
    description: 'California requires documented incident and abuse reporting procedures',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Abuse Reporting',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident and abuse reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented with SOC 341/341A forms available' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
        helpText: 'HSC 1796.42 requires reporting of suspected elder/dependent adult abuse per WIC 15630',
      },
      {
        id: 'q2',
        question: 'Which types of incidents does your reporting process cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical abuse' },
          { value: 'neglect', label: 'Neglect' },
          { value: 'financial', label: 'Financial abuse (SOC 342)' },
          { value: 'abandonment', label: 'Abandonment' },
          { value: 'isolation', label: 'Isolation' },
          { value: 'injury', label: 'Client injuries during service' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident and abuse reporting process is documented with required forms per HSC 1796.42.',
      atRisk: 'Incident reporting process exists but may not be fully documented. Ensure SOC 341/341A forms are available.',
      nonCompliant: 'No incident reporting process is in place. HSC 1796.42 requires reporting of suspected abuse per WIC 15630.',
      unknown: 'Verify your incident reporting procedures meet HSC 1796.42 requirements.',
    },
    stateNotes: [
      'Use SOC 341 for physical abuse and neglect',
      'Use SOC 342 for financial abuse',
      'Telephone report required immediately or as soon as practically possible',
      'Written report required within 2 working days',
    ],
  },

  {
    id: 'CA-A6',
    itemId: 'A6',
    stateCode: 'CA',
    title: 'Emergency Preparedness Plan',
    description: 'California requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Emergency Preparedness',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan including wildfires and earthquakes' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
        helpText: 'California agencies should address earthquakes, wildfires, power outages (PSPS), and service continuity',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and addresses California-specific hazards.',
      atRisk: 'Emergency preparedness plan is incomplete. Include earthquake, wildfire, and PSPS (Public Safety Power Shutoff) procedures.',
      nonCompliant: 'No emergency preparedness plan is in place. California agencies must plan for earthquakes, wildfires, and power shutoffs.',
      unknown: 'Review your emergency preparedness procedures for California-specific hazards.',
    },
    stateNotes: [
      'HSC 1796.42 requires emergency and disaster plans for California HCOs',
      'Plans must address earthquakes, wildfires, and Public Safety Power Shutoffs (PSPS)',
      'Include client notification procedures and service continuity protocols',
      'Tip: Coordinate with local Office of Emergency Services for regional hazard planning',
      'Maintain updated emergency contact lists for all clients and staff',
    ],
  },

  {
    id: 'CA-A7',
    itemId: 'A7',
    stateCode: 'CA',
    title: 'Complaint and Grievance Handling Process',
    description: 'California requires documented complaint handling procedures',
    severity: 'low',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Client Rights',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented process for handling client complaints and grievances?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented process with tracking' },
          { value: 'informal', label: 'Informal process, not fully documented' },
          { value: 'no', label: 'No complaint handling process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint and grievance handling process is documented per HSC 1796.42.',
      atRisk: 'Complaint handling process exists but may not be fully documented. Ensure clients receive written complaint procedures.',
      nonCompliant: 'No complaint handling process is in place. HSC 1796.42 requires written complaint procedures.',
      unknown: 'Verify your complaint procedures meet HSC 1796.42 requirements.',
    },
    stateNotes: [
      'HSC 1796.42 requires written complaint procedures provided to all clients',
      'Clients must receive CDSS contact information for filing complaints: (877) 424-5778',
      'Maintain a complaint log with resolution documentation for CDSS inspection',
      'Tip: Include complaint procedures in the initial service agreement packet',
    ],
  },

  {
    id: 'CA-A8',
    itemId: 'A8',
    stateCode: 'CA',
    title: 'Quality Assurance Reviews Documented',
    description: 'California recommends documented quality assurance processes',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Quality Assurance',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct and document quality assurance reviews?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented reviews (at least quarterly)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality assurance reviews' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance reviews are conducted regularly and documented.',
      atRisk: 'Quality assurance reviews are not conducted consistently or fully documented.',
      nonCompliant: 'No quality assurance reviews are conducted. Implement a regular QA process.',
      unknown: 'Consider implementing a documented quality assurance program.',
    },
    stateNotes: [
      'HSC 1796.42 supports quality assurance as part of HCO operational requirements',
      'CDSS inspectors may review QA documentation during licensure audits',
      'Include client satisfaction surveys and HCA performance reviews in QA program',
      'Tip: Conduct quarterly QA reviews and document findings with corrective actions',
    ],
  },

  {
    id: 'CA-A9',
    itemId: 'A9',
    stateCode: 'CA',
    title: 'License Posted and Visible',
    description: 'California requires HCO license to be posted in a conspicuous location',
    severity: 'low',
    regulationCode: 'HSC 1796.42',
    regulationName: 'License Display',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is your HCO license posted in your place of business in a conspicuous location visible to both clients and affiliated HCAs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per HSC 1796.42, license must be visible to clients and HCAs',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HCO license is properly posted per HSC 1796.42.',
      atRisk: '',
      nonCompliant: 'HCO license must be posted in a conspicuous location per HSC 1796.42. Post immediately.',
      unknown: 'Verify your license is properly posted.',
    },
    stateNotes: [
      'HSC 1796.42 requires HCO license to be posted in a conspicuous location',
      'License must be visible to both clients and affiliated HCAs',
      'CDSS inspectors will verify license posting during site visits',
      'Tip: Post license near the main entrance or reception area of your office',
    ],
  },

  {
    id: 'CA-A10',
    itemId: 'A10',
    stateCode: 'CA',
    title: 'Record Retention (7 Years)',
    description: 'California requires 7 years record retention',
    severity: 'medium',
    regulationCode: 'CA Civil Code',
    regulationName: 'Record Retention',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets CA requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'California requires retention for at least 7 years (longer for minors)',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets California\'s 7-year requirement.',
      atRisk: 'California requires 7-year record retention. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet California\'s 7-year requirement. Immediately update your retention policy.',
      unknown: 'Review California record retention requirements.',
    },
    stateNotes: [
      'California Civil Code and HSC 1796 series require minimum 7-year record retention',
      'Records for minor clients must be retained longer (until age 21 plus statute period)',
      'Includes personnel files, client records, training documentation, and financial records',
      'Tip: Implement secure electronic storage with backup for all required records',
      'Consider 10-year retention as best practice for litigation protection',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'CA-B1',
    itemId: 'B1',
    stateCode: 'CA',
    title: 'Personnel File Requirements',
    description: 'California requires specific documents in each HCA\'s personnel file',
    severity: 'high',
    regulationCode: 'HSC 1796.43-1796.45',
    regulationName: 'Personnel Records',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY Home Care Aide\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'livescan', label: 'Live Scan DOJ/FBI background check clearance' },
          { value: 'registry', label: 'HCA Registry enrollment confirmation' },
          { value: 'tb', label: 'TB clearance (within 90 days prior or 7 days after hire)' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'w4', label: 'W-4 tax form' },
          { value: 'training', label: 'Entry-level training documentation (5 hours)' },
          { value: 'orientation', label: 'Orientation training records (2 hours)' },
          { value: 'criminal_statement', label: 'Statement of prior criminal convictions (LIC 508)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all California-required documentation per HSC 1796.43-1796.45.',
      atRisk: 'Some personnel files are missing required documents. California requires: Live Scan clearance, HCA Registry enrollment, TB clearance, and training records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by California law. CDSS inspectors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against California HSC 1796.43-1796.45 requirements.',
    },
    stateNotes: [
      'HSC 1796.43-1796.45 specify required personnel file documentation for all HCAs',
      'Live Scan clearance (DOJ/FBI) must be obtained BEFORE client contact per HSC 1796.33',
      'HCA Registry enrollment confirmation is required per HSC 1796.23',
      'TB screening within 90 days prior or 7 days after hire per HSC 1796.45',
      'Tip: Create a standardized personnel file checklist for new hire onboarding',
    ],
  },

  {
    id: 'CA-B2',
    itemId: 'B2',
    stateCode: 'CA',
    title: 'Government-Issued Photo ID on File',
    description: 'California requires valid government-issued photo ID for all employees',
    severity: 'medium',
    regulationCode: 'HSC 1796.43',
    regulationName: 'Photo ID',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides have a current, valid government-issued photo ID on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have valid photo ID on file' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
        helpText: 'Required for Live Scan fingerprinting and employment verification',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs have valid government-issued photo ID on file per HSC 1796.43.',
      atRisk: 'Some HCAs are missing government-issued photo ID. Obtain copies immediately.',
      nonCompliant: 'Personnel files are missing required government-issued photo ID.',
      unknown: 'Verify all HCAs have valid government-issued photo ID on file.',
    },
    stateNotes: [
      'HSC 1796.43 requires government-issued photo ID for all HCAs',
      'Valid forms include California driver license, state ID, or US passport',
      'Photo ID is required for Live Scan fingerprinting per DOJ requirements',
      'Tip: Verify ID is current and not expired; update copies when renewed',
    ],
  },

  {
    id: 'CA-B3',
    itemId: 'B3',
    stateCode: 'CA',
    title: 'Signed Job Description on File',
    description: 'California requires job descriptions for all employees',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Job Descriptions',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides have a signed job description on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have signed job descriptions' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
        helpText: 'Job descriptions should outline duties, responsibilities, and limitations per HSC 1796.42',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs have signed job descriptions on file per HSC 1796.42.',
      atRisk: 'Some HCAs are missing signed job descriptions. Obtain signed copies immediately.',
      nonCompliant: 'Personnel files are missing required signed job descriptions.',
      unknown: 'Verify all HCAs have signed job descriptions on file.',
    },
    stateNotes: [
      'HSC 1796.42 requires clear documentation of HCA duties and responsibilities',
      'Job descriptions must outline scope of services and limitations',
      'Include prohibited activities (e.g., medication administration unless authorized)',
      'Tip: Have HCAs sign updated job descriptions annually during performance reviews',
    ],
  },

  {
    id: 'CA-B4',
    itemId: 'B4',
    stateCode: 'CA',
    title: 'HIPAA/Confidentiality Agreement Signed',
    description: 'California requires signed HIPAA and confidentiality agreements',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Confidentiality',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have signed HIPAA/confidentiality agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some HCAs are missing signed HIPAA/confidentiality agreements. This creates significant privacy risk.',
      nonCompliant: 'Personnel files are missing required HIPAA/confidentiality agreements.',
      unknown: 'Verify all HCAs have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      'HSC 1796.42 and California Civil Code 1798 require protection of client information',
      'HIPAA applies to HCOs that handle protected health information (PHI)',
      'California CMIA (Confidentiality of Medical Information Act) provides additional protections',
      'Tip: Include confidentiality training in orientation and obtain signed acknowledgment',
      'Violations can result in civil penalties and potential license revocation',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'CA-C1',
    itemId: 'C1',
    stateCode: 'CA',
    title: 'Live Scan DOJ/FBI Background Check (HSC 1796.33)',
    description: 'California requires Live Scan fingerprint-based background checks through DOJ and FBI',
    severity: 'high',
    regulationCode: 'HSC 1796.33',
    regulationName: 'Background Check',
    agencyName: 'California Department of Justice',
    agencyAbbr: 'DOJ',
    agencyPhone: '(916) 227-3835',
    agencyUrl: 'https://oag.ca.gov/fingerprints',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed Home Care Aides, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'doj', label: 'California DOJ Live Scan clearance' },
          { value: 'fbi', label: 'FBI fingerprint-based background check clearance' },
          { value: 'lic508', label: 'Criminal Record Statement (LIC 508)' },
          { value: 'guardian', label: 'Guardian system enrollment/verification' },
          { value: 'oig', label: 'OIG/Medicaid exclusion list verification' },
        ],
        required: true,
        helpText: 'California requires DOJ AND FBI Live Scan before client contact per HSC 1796.33',
      },
      {
        id: 'q2',
        question: 'Do any HCAs begin working with clients BEFORE their Live Scan clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if HCAs start before clearance (direct violation of HSC 1796.33 - no client contact allowed)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 2-3 checks but not all required
      { condition: 'countBetween', questionId: 'q1', value: [2, 3], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Live Scan DOJ/FBI background check documentation is complete for all HCAs per HSC 1796.33.',
      atRisk: 'Background check documentation may be incomplete. California requires DOJ AND FBI clearance. Review all HCA files.',
      nonCompliant: 'VIOLATION: HCAs are working before Live Scan clearance OR required checks are missing. Per HSC 1796.33, no client contact is permitted until DOJ AND FBI clearance is received. Immediately remove uncleared HCAs from client assignments.',
      unknown: 'Audit all HCA files for Live Scan clearance documentation per HSC 1796.33.',
    },
    stateNotes: [
      'Live Scan requires BOTH DOJ and FBI checks',
      'Use Guardian system to verify clearance status',
      'DOJ provides "rap back" notifications for subsequent arrests',
      'Certain crimes result in lifetime prohibition from working as HCA',
    ],
  },

  {
    id: 'CA-C2',
    itemId: 'C2',
    stateCode: 'CA',
    title: 'HCA Registry Status Verification',
    description: 'California requires verification of HCA Registry status before client assignment',
    severity: 'high',
    regulationCode: 'HSC 1796.23',
    regulationName: 'HCA Registry',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify HCA Registry status shows "Eligible" BEFORE assigning aides to clients?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verify "Eligible" status first' },
          { value: 'usually', label: 'Usually, but not always' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'HCAs must show "Eligible" status on Guardian/Registry before client contact',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HCA Registry status is verified before all client assignments per HSC 1796.23.',
      atRisk: 'Registry verification is not consistent. ALWAYS verify "Eligible" status before client contact.',
      nonCompliant: 'HCA Registry status is not being verified. This violates HSC 1796.23.',
      unknown: 'Implement HCA Registry verification process.',
    },
    stateNotes: [
      'HSC 1796.23 requires verification of "Eligible" status before ANY client assignment',
      'Use CDSS Guardian system at www.ccld.dss.ca.gov to check HCA status',
      'Registry status must show "Eligible" - not "Pending" or "Ineligible"',
      'Tip: Document registry verification date in client assignment records',
      'Check registry status periodically as it can change due to subsequent arrests',
    ],
  },

  {
    id: 'CA-C3',
    itemId: 'C3',
    stateCode: 'CA',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'California requires OIG and SAM exclusion list verification',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Exclusion List',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the OIG and SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly verification for all employees' },
          { value: 'hire', label: 'Yes, at hire only' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'CMS requires monthly exclusion list checks for Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list verification is conducted monthly for all employees.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG/SAM exclusion list verification is conducted. This creates significant Medicaid compliance risk.',
      unknown: 'Implement OIG/SAM exclusion list verification process.',
    },
    stateNotes: [
      'Federal law and California Medi-Cal require exclusion list verification',
      'Check OIG LEIE at exclusions.oig.hhs.gov and SAM at sam.gov',
      'CMS requires monthly verification for Medicaid/Medi-Cal providers',
      'Tip: Use automated screening services to ensure consistent monthly checks',
      'Employing excluded individuals can result in program exclusion and civil penalties',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'CA-D1',
    itemId: 'D1',
    stateCode: 'CA',
    title: 'TB Screening (HSC 1796.45)',
    description: 'California requires TB screening within 90 days prior or 7 days after hire, renewed every 2 years',
    severity: 'high',
    regulationCode: 'HSC 1796.45',
    regulationName: 'TB Screening',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides have current TB examination results on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have current TB examination (within 2 years)' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB examination required within 90 days prior to employment or within 7 days after employment per HSC 1796.45',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening expiration dates (every 2 years)?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all HCAs per HSC 1796.45 (renewed every 2 years).',
      atRisk: 'Some TB screenings may be expired. California requires TB examination every 2 years. Review all HCA files and schedule screening as needed.',
      nonCompliant: 'HCAs are working without valid TB examination results. This violates HSC 1796.45. Immediately obtain TB examination for all HCAs.',
      unknown: 'Verify TB screening status for all HCAs per HSC 1796.45.',
    },
    stateNotes: [
      'TB examination required within 90 days PRIOR to employment OR within 7 days AFTER employment',
      'Must be renewed every 2 years for negative tests',
      'Transferring HCAs can use certificate from prior employer if within 2 years',
      'Must prove free of ACTIVE tuberculosis disease',
    ],
  },

  {
    id: 'CA-D2',
    itemId: 'D2',
    stateCode: 'CA',
    title: 'Infection Control Program',
    description: 'California requires comprehensive infection control policies',
    severity: 'high',
    regulationCode: 'HSC 1796.44',
    regulationName: 'Infection Control',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program that includes? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'ppe', label: 'Personal protective equipment (PPE) guidelines' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures' },
          { value: 'outbreak', label: 'Disease outbreak response plan' },
          { value: 'training', label: 'Infection control training (included in 3-hour safety training)' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
        ],
        required: true,
        helpText: 'Infection control is part of required 3-hour safety training per HSC 1796.44',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets California HSC 1796.44 requirements.',
      atRisk: 'Infection control program is incomplete. Update policies to include all required elements per HSC 1796.44.',
      nonCompliant: 'Infection control program does not meet HSC 1796.44 requirements. Immediately develop comprehensive infection control policies.',
      unknown: 'Review infection control program against HSC 1796.44 requirements.',
    },
    stateNotes: [
      'HSC 1796.44 requires infection control as part of the 3-hour safety training',
      'Include hand hygiene, PPE use, and bloodborne pathogen procedures',
      'CalOSHA Bloodborne Pathogen Standard (8 CCR 5193) also applies',
      'Tip: Provide annual infection control refresher training and document completion',
      'Maintain exposure control plan as required by CalOSHA',
    ],
  },

  {
    id: 'CA-D3',
    itemId: 'D3',
    stateCode: 'CA',
    title: 'CPR/First Aid Training',
    description: 'California recommends CPR and First Aid training for HCAs',
    severity: 'medium',
    regulationCode: 'HSC 1796.44',
    regulationName: 'Training',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do your Home Care Aides have current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have current CPR certification' },
          { value: 'most', label: 'Most have current certification' },
          { value: 'no', label: 'No / not required by our agency' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your Home Care Aides have current First Aid certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs have current First Aid certification' },
          { value: 'most', label: 'Most have current certification' },
          { value: 'no', label: 'No / not required by our agency' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs have current CPR and First Aid certifications.',
      atRisk: 'Some HCAs have expired or missing CPR/First Aid certifications. Schedule training immediately.',
      nonCompliant: 'HCAs lack CPR/First Aid certifications. While not strictly required by CDSS, this is a best practice for client safety.',
      unknown: 'Verify CPR and First Aid certifications for all HCAs.',
    },
    stateNotes: [
      'CPR/First Aid is recommended but not mandated by HSC 1796.44 for basic home care',
      'Many California HCOs require it as best practice and for liability protection',
      'Certifications from American Red Cross or American Heart Association are widely accepted',
      'Tip: Include CPR/First Aid in onboarding and track renewal dates (typically every 2 years)',
    ],
  },

  {
    id: 'CA-D4',
    itemId: 'D4',
    stateCode: 'CA',
    title: 'PPE Availability and Training',
    description: 'California requires PPE availability and training for HCAs',
    severity: 'high',
    regulationCode: 'HSC 1796.44',
    regulationName: 'PPE',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is appropriate PPE available to ALL Home Care Aides?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, PPE is readily available to all HCAs' },
          { value: 'some', label: 'Available but not consistently' },
          { value: 'no', label: 'PPE is not provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do HCAs receive training on proper PPE use (included in 3-hour safety training)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE is available to all HCAs with proper training per HSC 1796.44.',
      atRisk: 'PPE availability or training is inconsistent. Ensure all HCAs have access and training.',
      nonCompliant: 'PPE is not provided to HCAs. This violates HSC 1796.44 and CalOSHA requirements.',
      unknown: 'Verify PPE availability and training for all HCAs.',
    },
    stateNotes: [
      'HSC 1796.44 includes PPE training as part of the 3-hour safety requirement',
      'CalOSHA requires employers to provide PPE at no cost to employees (8 CCR 3380)',
      'Include gloves, masks, gowns, and eye protection as appropriate for tasks',
      'Tip: Maintain PPE inventory and distribution logs for CalOSHA compliance',
      'Train HCAs on proper donning, doffing, and disposal of PPE',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'CA-E1',
    itemId: 'E1',
    stateCode: 'CA',
    title: 'Entry-Level Training (HSC 1796.44) - 5 Hours Required',
    description: 'California requires 5 hours of entry-level training: 2 hours orientation + 3 hours safety/infection control',
    severity: 'high',
    regulationCode: 'HSC 1796.44',
    regulationName: 'Entry Training',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of entry-level training do ALL new HCAs complete BEFORE client contact?',
        inputType: 'number',
        required: true,
        helpText: 'California requires minimum 5 hours: 2 hours orientation + 3 hours safety/infection control',
      },
      {
        id: 'q2',
        question: 'Does entry-level training include the following required components? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'orientation_2hr', label: '2 hours orientation (role as caregiver, terms of employment)' },
          { value: 'safety_3hr', label: '3 hours safety training (basic safety, emergency procedures, infection control)' },
          { value: 'documentation', label: 'Training documentation in personnel file' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 5 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 3 },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HCA entry-level training meets California HSC 1796.44 requirements (5 hours: 2 orientation + 3 safety).',
      atRisk: 'Entry-level training may not meet HSC 1796.44 requirements. Ensure all HCAs complete 5 hours (2 orientation + 3 safety) BEFORE client contact.',
      nonCompliant: 'HCAs are providing services without required 5-hour entry-level training. This violates HSC 1796.44. Immediately complete training for any HCA who started without it.',
      unknown: 'Review HCA training program against HSC 1796.44 requirements.',
    },
    stateNotes: [
      '2 hours orientation: role as caregiver, terms of employment',
      '3 hours safety: basic safety precautions, emergency procedures, infection control',
      'Training may be completed online per HSC 1796.44(d)',
      'Must be completed BEFORE presence with client',
    ],
  },

  {
    id: 'CA-E2',
    itemId: 'E2',
    stateCode: 'CA',
    title: 'Annual Training (HSC 1796.44) - 5 Hours Required',
    description: 'California requires 5 hours of annual training on core competencies',
    severity: 'high',
    regulationCode: 'HSC 1796.44',
    regulationName: 'Annual Training',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of annual training do ALL HCAs complete each year?',
        inputType: 'number',
        required: true,
        helpText: 'California requires minimum 5 hours of annual training per HSC 1796.44',
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'rights_safety', label: 'Clients\' rights and safety' },
          { value: 'daily_living', label: 'How to provide for and respond to client\'s daily living needs' },
          { value: 'abuse', label: 'How to report, prevent, and detect abuse and neglect' },
          { value: 'hygiene', label: 'How to assist client with personal hygiene and other home care services' },
          { value: 'transport', label: 'How to safely transport a client (if applicable)' },
        ],
        helpText: 'HSC 1796.44(c) specifies required annual training topics',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 5 },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 3 },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets California HSC 1796.44 requirements (5 hours with required topics).',
      atRisk: 'Some HCAs may be behind on annual training or topics are incomplete. HSC 1796.44 requires 5 hours annually on specified topics.',
      nonCompliant: 'Annual training does not meet HSC 1796.44 requirements. Implement a 5-hour annual training program with required topics immediately.',
      unknown: 'Review annual training program against HSC 1796.44 requirements.',
    },
    stateNotes: [
      'Annual training must cover core competencies and be population-specific',
      'Required topics: client rights/safety, daily living needs, abuse reporting, personal hygiene, transport (if applicable)',
      'Training may be completed online per HSC 1796.44(d)',
    ],
  },

  {
    id: 'CA-E3',
    itemId: 'E3',
    stateCode: 'CA',
    title: 'Mandated Reporter Training (WIC 15630)',
    description: 'All HCAs are mandated reporters of elder and dependent adult abuse',
    severity: 'high',
    regulationCode: 'WIC 15630',
    regulationName: 'Mandated Reporting',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(833) 401-0832',
    agencyUrl: 'https://cdss.ca.gov/MandatedReporting',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Care Aides receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HCAs are trained (using CDSS online course or equivalent)' },
          { value: 'most', label: 'Most HCAs are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Training required within 60 days of employment per California law',
      },
      {
        id: 'q2',
        question: 'Do HCAs know how to use Form SOC 341 (abuse/neglect) and SOC 342 (financial abuse)?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all trained on proper forms' },
          { value: 'some', label: 'Some are trained' },
          { value: 'no', label: 'No formal training on forms' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'some' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs are trained on mandated reporter duties per WIC 15630 and know how to use SOC 341/342 forms.',
      atRisk: 'Some HCAs may lack mandated reporter training. Per WIC 15630, all HCAs are mandated reporters. Complete training within 60 days of employment.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates WIC 15630. HCAs who fail to report suspected abuse can face misdemeanor charges, up to 6 months jail, and $1,000 fine.',
      unknown: 'Verify all HCAs have received mandated reporter training per WIC 15630.',
    },
    stateNotes: [
      'Use Form SOC 341 for physical abuse and neglect',
      'Use Form SOC 342 for financial abuse',
      'Telephone report required immediately or as soon as practically possible',
      'Written report required within 2 working days',
      'Free online training available at cdss.ca.gov/MandatedReporting',
    ],
  },

  {
    id: 'CA-E4',
    itemId: 'E4',
    stateCode: 'CA',
    title: 'Competency Evaluations Documented',
    description: 'California requires documented competency evaluations for HCAs',
    severity: 'medium',
    regulationCode: 'HSC 1796.44',
    regulationName: 'Competency',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL Home Care Aides have documented competency evaluations on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have documented competency evaluations' },
          { value: 'most', label: 'Most have evaluations, some are missing' },
          { value: 'no', label: 'No competency evaluations documented' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HCAs have documented competency evaluations.',
      atRisk: 'Some HCAs are missing competency evaluations. Complete evaluations for all.',
      nonCompliant: 'No competency evaluations are documented. Implement competency evaluation process.',
      unknown: 'Verify all HCAs have documented competency evaluations.',
    },
    stateNotes: [
      'HSC 1796.44 training requirements imply competency verification',
      'Document skills assessment for each HCA in their personnel file',
      'Evaluate competency in areas covered by annual training requirements',
      'Tip: Use standardized competency checklists signed by supervisor and HCA',
      'Conduct competency re-evaluation when HCAs take on new or complex clients',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'CA-F1',
    itemId: 'F1',
    stateCode: 'CA',
    title: 'Written Service Agreement Requirements',
    description: 'Written service agreement required with each client',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Service Agreement',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written service agreement for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal service agreement process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your service agreements include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'rates', label: 'Rates and payment terms' },
          { value: 'cancellation', label: 'Cancellation policy' },
          { value: 'rights', label: 'Client rights' },
          { value: 'signature', label: 'Client/representative signature' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service agreements are completed before services begin and include all required elements per HSC 1796.42.',
      atRisk: 'Service agreements may be incomplete or created after services begin. HSC 1796.42 requires written agreements before first service.',
      nonCompliant: 'Services are being provided without written service agreements. This violates HSC 1796.42. Immediately create service agreements for all clients.',
      unknown: 'Review service agreement process against HSC 1796.42 requirements.',
    },
    stateNotes: [
      'HSC 1796.42 requires written service agreements BEFORE services begin',
      'Agreement must include services, frequency, rates, and cancellation policy',
      'Provide client with copy of signed agreement and retain original in client file',
      'Tip: Include client rights statement and CDSS complaint information in agreement',
      'Update agreement when services or rates change and obtain new signatures',
    ],
  },

  {
    id: 'CA-F2',
    itemId: 'F2',
    stateCode: 'CA',
    title: 'Supervision of Home Care Aides',
    description: 'California requires supervision of HCAs',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Supervision',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often does a supervisor evaluate each Home Care Aide\'s performance?',
        inputType: 'select',
        options: [
          { value: 'every_90', label: 'Every 90 days or more frequently' },
          { value: 'every_180', label: 'Every 180 days' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No formal supervision schedule' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is supervision documented in personnel and client records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always documented' },
          { value: 'no', label: 'No / inconsistently' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no schedule OR not documented
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'never' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: regular supervision and documented
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['every_90', 'every_180'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: annual supervision (infrequent but exists)
      { condition: 'equals', questionId: 'q1', value: 'annually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets California requirements per HSC 1796.42 with proper documentation.',
      atRisk: 'Supervision frequency or documentation needs improvement. Regular supervision with documentation is required.',
      nonCompliant: 'HCAs are not receiving required supervision. Implement supervision schedule immediately.',
      unknown: 'Review supervision practices against HSC 1796.42 requirements.',
    },
    stateNotes: [
      'HSC 1796.42 requires HCOs to maintain supervision of affiliated HCAs',
      'Supervision should include periodic performance evaluations and client feedback',
      'Document all supervisory visits and contacts in both personnel and client files',
      'Tip: Conduct unannounced supervisory visits to ensure quality of care',
      'Address performance issues promptly with documented corrective action plans',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'CA-G1',
    itemId: 'G1',
    stateCode: 'CA',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Record Tracking',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'hco_license', label: 'HCO license (renews every 2 years)' },
          { value: 'hca_registry', label: 'HCA Registry renewals (every 2 years)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation' },
          { value: 'dishonesty_bond', label: 'Dishonesty bond' },
          { value: 'tb', label: 'TB screenings (every 2 years)' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk for HCO license, HCA Registry, TB screenings, and training requirements.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'HSC 1796.35 requires HCO license renewal every 2 years',
      'HSC 1796.23(d) requires HCA Registry renewal every 2 years',
      'HSC 1796.45 requires TB screening renewal every 2 years',
      'Tip: Set calendar reminders 90 days before each expiration date',
      'Use compliance software to automate tracking and notifications',
    ],
  },

  {
    id: 'CA-G2',
    itemId: 'G2',
    stateCode: 'CA',
    title: 'Audit-Ready Documentation',
    description: 'Personnel files organized for CDSS inspector review',
    severity: 'medium',
    regulationCode: 'HSC 1796.42',
    regulationName: 'Audit Readiness',
    agencyName: 'California Department of Social Services',
    agencyAbbr: 'CDSS',
    agencyPhone: '(877) 424-5778',
    agencyUrl: 'https://www.cdss.ca.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are your personnel files organized and accessible for CDSS inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any HCA\'s complete personnel file within 5 minutes if CDSS arrived for an inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files are well-organized and ready for CDSS inspection.',
      atRisk: 'File organization needs improvement. CDSS inspectors expect immediate access to complete personnel files.',
      nonCompliant: 'Disorganized files will result in inspection deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against CDSS inspection requirements.',
    },
    stateNotes: [
      'CDSS conducts inspections per HSC 1796.69 to verify compliance',
      'Inspectors may request any HCA personnel file for immediate review',
      'Organize files with tabs or sections for each required document category',
      'Tip: Conduct monthly internal audits to ensure files are complete and current',
      'Maintain a file checklist in each personnel folder showing document status',
    ],
  },

  {
    id: 'CA-G3',
    itemId: 'G3',
    stateCode: 'CA',
    title: 'HIPAA Compliance Documentation',
    description: 'California requires HIPAA compliance documentation',
    severity: 'high',
    regulationCode: '45 CFR 160-164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(877) 696-6775',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do you have documented HIPAA policies and procedures?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive HIPAA compliance program' },
          { value: 'basic', label: 'Basic policies, needs improvement' },
          { value: 'no', label: 'No HIPAA documentation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have you conducted a HIPAA risk assessment within the past year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'basic' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance documentation is comprehensive with annual risk assessment completed.',
      atRisk: 'HIPAA compliance documentation or risk assessment needs attention. Review and update immediately.',
      nonCompliant: 'No HIPAA documentation exists. This creates significant legal and regulatory risk.',
      unknown: 'Verify HIPAA compliance documentation is complete.',
    },
    stateNotes: [
      'HIPAA (45 CFR 160-164) applies to HCOs handling protected health information',
      'California CMIA (Civil Code 56-56.37) provides additional privacy protections',
      'Annual HIPAA risk assessment is required under 45 CFR 164.308(a)(1)',
      'Tip: Maintain a HIPAA compliance binder with policies, training records, and risk assessments',
      'Document any breach incidents and corrective actions per HSC 1796.42',
    ],
  },
];

export default CALIFORNIA_QUESTIONS;